import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { ManualSection, USER_MANUAL_SECTIONS } from './user-manual-content';
import { AssetHeaderComponent, AssetHeaderConfig } from '../asset-header/asset-header.component';
import { ManualRoleAudienceTableComponent } from './manual-role-audience-table.component';
import { ManualBoldLabelsPipe } from './manual-bold-labels.pipe';

@Component({
  selector: 'app-user-manual-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    ManualRoleAudienceTableComponent,
    ManualBoldLabelsPipe,
  ],
  templateUrl: './user-manual-section.component.html',
  styleUrl: './user-manual-section.component.scss',
})
export class UserManualSectionComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  topSection: ManualSection | null = null;
  subSections: ManualSection[] = [];
  level1Sections: ManualSection[] = USER_MANUAL_SECTIONS.filter((s) => s.level === 1);
  assetHeaderConfig: AssetHeaderConfig | null = null;

  constructor() {
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      const sectionId = String(params.get('sectionId') || '');
      this.applySection(sectionId);
    });
  }

  private applySection(sectionId: string): void {
    this.topSection = USER_MANUAL_SECTIONS.find((s) => s.id === sectionId) ?? null;

    if (!this.topSection) {
      this.subSections = [];
      this.assetHeaderConfig = null;
      return;
    }

    const topKey = this.getTopLevelKey(this.topSection.title);
    this.subSections = USER_MANUAL_SECTIONS.filter(
      (s) => this.getTopLevelKey(s.title) === topKey && s.id !== this.topSection?.id,
    );

    const topTitle = this.topSection.title;
    this.assetHeaderConfig = {
      breadcrumbs: [
        { label: 'Home', route: '/' },
        { label: 'User Manual', route: '/user-manual' },
        { label: topTitle },
      ],
      title: topTitle,
      description: 'Detailed view of this user manual section.',
    };
  }

  openSection(sectionId: string): void {
    this.router.navigate(['/user-manual', sectionId]);
  }

  /** Avoid an empty bordered card when a subsection has no text, blocks, or figures. */
  hasSubsectionBody(sec: ManualSection): boolean {
    if (sec.contentBlocks?.length) {
      return true;
    }
    if (sec.roleAudienceTable?.length) {
      return true;
    }
    if (sec.figures?.length) {
      return true;
    }
    return (sec.content ?? []).some((p) => (p ?? '').trim().length > 0);
  }

  private getTopLevelKey(title: string): string {
    const m = title.match(/^(\d+)\./);
    return m?.[1] ?? '0';
  }
}
